import CancellableHelper from "@/requests/CancellableHelper";
import { AdministrationOffice } from "@/models/api/administration/offices/AdministrationOffice";
import { OfficeProfileResult } from "@/models/api/administration/servicesAndAttributes/ServiceTypeExportConfiguration";
import { ContractorDataModel } from "@/models/api/administration/offices/ContractorDataModel";
import { OfficeSettingsRest } from "@/models/api/administration/offices/OfficeSettingsRest";
import { DocumentTemplate, DocumentTemplateAddEdit } from "@/models/api/administration/offices/DocumentTemplate";
import { OfficeCashBox } from "@/models/api/administration/offices/OfficeCashBox";
import { OfficeOwner, OfficeProfileOwner } from "@/models/api/administration/offices/OfficeOwner";
import { OfficeDocumentType } from "@/models/api/administration/offices/OfficeDocumentType";
import { axiosInstance } from "@/plugins/axios";
import { AxiosResponse } from "axios";
import { OfficeSignature } from "@/models/api/administration/offices/OfficeSignature";
import { OfficeUser } from "@/models/api/administration/offices/OfficeUser";
import {
  OfficeDetails,
  OfficeProfileDetails,
  OfficeProfileInfo,
} from "@/models/api/administration/offices/OfficeProfile";
import { DefaultInvoiceSendSettings } from "@/models/api/administration/offices/DefaultInvoiceSendSettings";

//#region Routes
export const URL_GET_OFFICE_PROFILES = "/Office/OfficeProfiles";
export const URL_GET_OFFICE_PROFILES_BY_OFFICE_ID = "/Office/OfficeProfilesByOfficeId";

export const URL_GET_ADMINISTRATION_OFFICES = "/Office/Administration";
export const URL_GET_OFFICE_DETAILS = "/Office/${OfficeId}/Details";
export const URL_POST_EDIT_OFFICE_DETAILS = "/Office/${OfficeId}/Edit";

export const URL_GET_CONTRACTOR_DETAILS = "/Office/ContractorDetails";

export const URL_GET_SETTINGS_BY_OFFICE = "/Office/SettingsByOffice";
export const URL_GET_DEFAULT_INVOICE_SEND_SETTINGS = "/Office/${OfficeId}/DefaultInvoiceSendSettings";
export const URL_POST_DEFAULT_INVOICE_SEND_SETTINGS = "/Office/${OfficeId}/DefaultInvoiceSendSettings";
export const URL_POST_ENABLE_ALL_CONTRACTOR_REMIND_ABOUT_NOT_SEND_EMAIL =
  "/Office/${OfficeId}/EnableAllContractorRemindAboutNotSendEmail";
export const URL_POST_UPDATE_SETTINGS_BY_OFFICE = "/Office/UpdateSettingsByOffice";
export const URL_POST_UPDATE_DOCUMENT_IMAGE = "/Office/${OfficeId}/UpdateDocumentImage/${Type}";
export const URL_GET_DOCUMENT_IMAGE = "/Office/${OfficeId}/GetDocumentImage/${Type}";

export const URL_GET_DOCUMENT_TEMPLATES = "/Office/DocumentTemplatesByOfficeProfileId";
export const URL_GET_DOCUMENT_TEMPLATE_TYPES = "/Office/DocumentTemplateTypes";
export const URL_POST_ADD_DOCUMENT_TEMPLATES = "/Office/AddDocumentTemplateToOfficeProfileId";
export const URL_POST_EDIT_DOCUMENT_TEMPLATES = "/Office/EditDocumentTemplateInOfficeProfileId";

export const URL_GET_CASH_REGISTERS = "/Office/CashRegistersByOfficeId";
export const URL_POST_ADD_CASH_REGISTER = "/Office/AddCashRegistersToOffice";
export const URL_POST_REMOVE_CASH_REGISTER = "/Office/RemoveCashRegisterFromOffice";
export const URL_POST_UPDATE_CASH_REGISTER = "/Office/UpdateCashRegister";

export const URL_GET_CASHIERS = "/Office/Cashiers";
export const URL_GET_CASHIERS_OFFICE_PROFILE_OWNERS = "/Office/CashiersOfficeProfileOwners";

export const URL_GET_TRADERS = "/Office/Traders";
export const URL_GET_TRADERS_OFFICE_PROFILE_OWNERS = "/Office/TradersOfficeProfileOwners";

export const URL_GET_USERS_FROM_OFFICE = "/Office/GetUsersFromOffice";
export const URL_POST_ADD_OWNER = "/Office/AddOwner";
export const URL_POST_ADD_PROFILE_OWNER = "/Office/AddProfileOwner";
export const URL_POST_REMOVE_PROFILE_OWNER = "/Office/RemoveProfileOwner";

export const URL_GET_SIGNATURES_INFO = "/Office/${OfficeId}/GetSignaturesInfo";
export const URL_POST_UPLOAD_CERTIFICATE = "/Office/${OfficeId}/Signature/UploadCertificate";
export const URL_POST_UPLOAD_IMAGE = "/Office/${OfficeId}/Signature/UploadImage";
export const URL_POST_REMOVE_SIGNATURE_CERTIFICATE = "/Office/${OfficeId}/RemoveSignatureCertificate";
export const URL_POST_REMOVE_SIGNATURE_IMAGE = "/Office/${OfficeId}/RemoveSignatureImage";

export const URL_GET_OFFICE_PROFILE_LIST = "/Office/${OfficeId}/OfficeProfiles";
export const URL_POST_ADD_OFFICE_PROFILE = "/Office/${OfficeId}/Add";
export const URL_GET_OFFICE_PROFILE_DETAILS = "/Office/${OfficeId}/OfficeProfile/${OfficeProfileId}/Details";
export const URL_POST_UPDATE_OFFICE_PROFILE = "/Office/${OfficeId}/OfficeProfile/${OfficeProfileId}/Update";
//#endregion

//#region Common
const _getOfficeProfiles = new CancellableHelper<Array<OfficeProfileResult>>(
  URL_GET_OFFICE_PROFILES,
  OfficeProfileResult
);
export const getOfficeProfiles = async (): Promise<Array<OfficeProfileResult>> => {
  return await _getOfficeProfiles.get();
};

const _getOfficeProfilesByOfficeId = new CancellableHelper<Array<OfficeProfileResult>>(
  URL_GET_OFFICE_PROFILES_BY_OFFICE_ID,
  OfficeProfileResult
);
export const getOfficeProfilesByOfficeId = async (officeId: string): Promise<Array<OfficeProfileResult>> => {
  return await _getOfficeProfilesByOfficeId.get({ OfficeId: officeId });
};
//#endregion

//#region Basic Information
const _getAdministrationOffices = new CancellableHelper<Array<AdministrationOffice>>(
  URL_GET_ADMINISTRATION_OFFICES,
  AdministrationOffice
);
export const getAdministrationOffices = async (): Promise<Array<AdministrationOffice>> => {
  return await _getAdministrationOffices.get();
};

const _getOfficeDetails = new CancellableHelper<OfficeDetails>(URL_GET_OFFICE_DETAILS, OfficeDetails);
export const getOfficeDetails = async (officeId: string): Promise<OfficeDetails> => {
  return _getOfficeDetails.get({ OfficeId: officeId });
};

const _updateOfficeDetails = new CancellableHelper<null>(URL_POST_EDIT_OFFICE_DETAILS);
export const updateOfficeDetails = async (officeId: string, officeDetails: OfficeDetails): Promise<null> => {
  return _updateOfficeDetails.post({ OfficeId: officeId }, officeDetails);
};
//#endregion

//#region Address
const _getContractorDetails = new CancellableHelper<ContractorDataModel>(
  URL_GET_CONTRACTOR_DETAILS,
  ContractorDataModel
);
export const getContractorDetails = async (officeId: string): Promise<ContractorDataModel> => {
  return _getContractorDetails.get({ OfficeId: officeId });
};
//#endregion

//#region Office Settings Rest
const _getSettingsByOffice = new CancellableHelper<OfficeSettingsRest>(URL_GET_SETTINGS_BY_OFFICE, OfficeSettingsRest);
export const getSettingsByOffice = async (officeId: string): Promise<OfficeSettingsRest> => {
  return _getSettingsByOffice.get({ OfficeId: officeId });
};

const _getDefaultInvoiceSendSettings = new CancellableHelper<DefaultInvoiceSendSettings>(
  URL_GET_DEFAULT_INVOICE_SEND_SETTINGS,
  DefaultInvoiceSendSettings
);
export const getDefaultInvoiceSendSettings = async (officeId: string): Promise<DefaultInvoiceSendSettings> => {
  return _getDefaultInvoiceSendSettings.get({ OfficeId: officeId });
};

const _saveDefaultInvoiceSendSetting = new CancellableHelper<null>(URL_GET_DEFAULT_INVOICE_SEND_SETTINGS, null, true);
export const saveDefaultInvoiceSendSetting = async (
  officeId: string,
  model: DefaultInvoiceSendSettings
): Promise<null> => {
  return _saveDefaultInvoiceSendSetting.post({ OfficeId: officeId }, model);
};

const _postUpdateSettingsByOffice = new CancellableHelper<null>(URL_POST_UPDATE_SETTINGS_BY_OFFICE, null, true);
export const postUpdateSettingsByOffice = async (officeId: string, model: OfficeSettingsRest): Promise<null> => {
  return _postUpdateSettingsByOffice.post({ OfficeId: officeId }, model);
};

const _turnOnSendRemindersForEveryone = new CancellableHelper<null>(
  URL_POST_ENABLE_ALL_CONTRACTOR_REMIND_ABOUT_NOT_SEND_EMAIL,
  null,
  true
);
export const turnOnSendRemindersForEveryone = async (officeId: string): Promise<null> => {
  return _turnOnSendRemindersForEveryone.post({ OfficeId: officeId });
};

export const postUpdateImage = async (file: File, officeId: string, imageType: number) => {
  const url = URL_POST_UPDATE_DOCUMENT_IMAGE.replace("${OfficeId}", officeId).replace("${Type}", imageType.toString());
  const formData = new FormData();
  formData.append(file.name, file);

  return axiosInstance.post(url, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

// export const getDocumentImage = async (officeId: string, imageType: number) => {
//   const response = await axiosInstance.get(URL_GET_DOCUMENT_IMAGE.replace("${OfficeId}", officeId).replace("${Type}", imageType.toString()), {
//     responseType: "blob",
//     });
//   const blob = new Blob([response.data], {
//     type: response.headers["content-type"],
//   });
//   return new File(blob, )
// };

const _getDocumentImage = new CancellableHelper<File>(URL_GET_DOCUMENT_IMAGE);
_getDocumentImage.isFile = true;
export const getDocumentImage = async (officeId: string, imageType: number): Promise<File> => {
  return await _getDocumentImage.get({
    OfficeId: officeId,
    Type: imageType,
  });
};

//#endregion

//#region Documents Registers
const _getDocumentTemplates = new CancellableHelper<Array<DocumentTemplate>>(
  URL_GET_DOCUMENT_TEMPLATES,
  DocumentTemplate
);
export const getDocumentTemplates = async (officeProfileId: number): Promise<Array<DocumentTemplate>> => {
  return _getDocumentTemplates.get({ OfficeProfileId: officeProfileId });
};

const _getDocumentTemplateTypes = new CancellableHelper<Array<OfficeDocumentType>>(
  URL_GET_DOCUMENT_TEMPLATE_TYPES,
  OfficeDocumentType
);
export const getDocumentTemplateTypes = async (): Promise<Array<OfficeDocumentType>> => {
  return _getDocumentTemplateTypes.get();
};

const _postAddDocumentTemplates = new CancellableHelper<null>(URL_POST_ADD_DOCUMENT_TEMPLATES);
export const postAddDocumentTemplates = async (
  officeProfileId: number,
  documentTemplate: DocumentTemplateAddEdit
): Promise<null> => {
  return _postAddDocumentTemplates.post({ OfficeProfileId: officeProfileId }, documentTemplate);
};

const _postEditDocumentTemplates = new CancellableHelper<null>(URL_POST_EDIT_DOCUMENT_TEMPLATES);
export const postEditDocumentTemplates = async (
  officeProfileId: number,
  documentTemplate: DocumentTemplateAddEdit
): Promise<null> => {
  return _postEditDocumentTemplates.post({ OfficeProfileId: officeProfileId }, documentTemplate);
};
//#endregion

//#region Cash Registers
const _getCashRegisters = new CancellableHelper<Array<OfficeCashBox>>(URL_GET_CASH_REGISTERS, OfficeCashBox);
export const getCashRegisters = async (officeId: string): Promise<Array<OfficeCashBox>> => {
  return _getCashRegisters.get({ OfficeId: officeId });
};

const _postAddCashRegister = new CancellableHelper<OfficeCashBox>(URL_POST_ADD_CASH_REGISTER);
export const addCashRegister = async (cashBox: OfficeCashBox): Promise<OfficeCashBox> => {
  return _postAddCashRegister.post(null, cashBox);
};

const _postRemoveCashRegister = new CancellableHelper<OfficeCashBox>(URL_POST_REMOVE_CASH_REGISTER);
export const removeCashRegister = async (cashBoxId: number): Promise<OfficeCashBox> => {
  return _postRemoveCashRegister.post({ CashBoxId: cashBoxId });
};

const _postEditCashRegister = new CancellableHelper<OfficeCashBox>(URL_POST_UPDATE_CASH_REGISTER);
export const editCashRegister = async (cashBox: OfficeCashBox): Promise<OfficeCashBox> => {
  return _postEditCashRegister.post(null, cashBox);
};

//#endregion

//#region Cashiers
const _getCashiers = new CancellableHelper<Array<OfficeOwner>>(URL_GET_CASHIERS, OfficeOwner);
export const getCashiers = async (officeId: string, officeProfileId: number): Promise<Array<OfficeOwner>> => {
  return _getCashiers.get({
    OfficeId: officeId,
    OfficeProfileId: officeProfileId,
  });
};

const _getCashiersOfficeProfileOwners = new CancellableHelper<Array<OfficeProfileOwner>>(
  URL_GET_CASHIERS_OFFICE_PROFILE_OWNERS,
  OfficeProfileOwner
);
export const getCashiersOfficeProfileOwners = async (officeProfileId: number): Promise<Array<OfficeProfileOwner>> => {
  return _getCashiersOfficeProfileOwners.get({
    OfficeProfileId: officeProfileId,
  });
};

//#endregion

//#region Traders
const _getTraders = new CancellableHelper<Array<OfficeOwner>>(URL_GET_TRADERS, OfficeOwner);
export const getTraders = async (officeId: string, officeProfileId: number): Promise<Array<OfficeOwner>> => {
  return _getTraders.get({
    OfficeId: officeId,
    OfficeProfileId: officeProfileId,
  });
};

const _getTradersOfficeProfileOwners = new CancellableHelper<Array<OfficeProfileOwner>>(
  URL_GET_TRADERS_OFFICE_PROFILE_OWNERS,
  OfficeProfileOwner
);
export const getTradersOfficeProfileOwners = async (officeProfileId: number): Promise<Array<OfficeProfileOwner>> => {
  return _getTradersOfficeProfileOwners.get({
    OfficeProfileId: officeProfileId,
  });
};
//#endregion

//#region Cashiers and Traders
const _getUsersFromOffice = new CancellableHelper<Array<OfficeUser>>(URL_GET_USERS_FROM_OFFICE, OfficeUser);
export const getUsersFromOffice = async (officeId: string): Promise<Array<OfficeUser>> => {
  return _getUsersFromOffice.get({ OfficeId: officeId });
};

const _addOwner = new CancellableHelper<void>(URL_POST_ADD_OWNER);
export const addOwner = async (
  userId: string,
  officeId: string,
  userName: string,
  ownerType: number
): Promise<void> => {
  return _addOwner.post({
    UserId: userId,
    OfficeId: officeId,
    UserName: userName,
    OwnerType: ownerType,
  });
};

const _addProfileOwner = new CancellableHelper<void>(URL_POST_ADD_PROFILE_OWNER);
export const addProfileOwner = async (officeProfileId: number, ownerId: number): Promise<void> => {
  return _addProfileOwner.post({
    OfficeProfileId: officeProfileId,
    OwnerId: ownerId,
  });
};

const _removeProfileOwner = new CancellableHelper<void>(URL_POST_REMOVE_PROFILE_OWNER);
export const removeProfileOwner = async (officeProfileId: number, ownerId: number): Promise<void> => {
  return _removeProfileOwner.post({
    OfficeProfileId: officeProfileId,
    OwnerId: ownerId,
  });
};
//#endregion

//#region Signatures
const _getSignaturesInfo = new CancellableHelper<OfficeSignature>(URL_GET_SIGNATURES_INFO, OfficeSignature);
export const getSignaturesInfo = async (officeId: string): Promise<OfficeSignature> => {
  return _getSignaturesInfo.get({ OfficeId: officeId });
};

export const uploadSignatureCertificate = async (file: File, officeId: string): Promise<AxiosResponse> => {
  const url = URL_POST_UPLOAD_CERTIFICATE.replace("${OfficeId}", officeId);
  const formData = new FormData();
  formData.append(file.name, file);

  return axiosInstance.post(url, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

export const uploadSignatureImage = async (file: File, officeId: string): Promise<AxiosResponse> => {
  const url = URL_POST_UPLOAD_IMAGE.replace("${OfficeId}", officeId);
  const formData = new FormData();
  formData.append(file.name, file);

  return axiosInstance.post(url, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

const _removeSignatureCertificate = new CancellableHelper<null>(URL_POST_REMOVE_SIGNATURE_CERTIFICATE);
export const removeSignatureCertificate = async (officeId: string): Promise<null> => {
  return _removeSignatureCertificate.post({ OfficeId: officeId });
};

const _removeSignatureImage = new CancellableHelper<null>(URL_POST_REMOVE_SIGNATURE_IMAGE);
export const removeSignatureImage = async (officeId: string): Promise<null> => {
  return _removeSignatureImage.post({ OfficeId: officeId });
};

//#endregion

//#region OfficeProfile
const _getOfficeProfileList = new CancellableHelper<Array<OfficeProfileInfo>>(URL_GET_OFFICE_PROFILE_LIST);
export const getOfficeProfileList = async (officeId: string): Promise<Array<OfficeProfileInfo>> => {
  return _getOfficeProfileList.get({ OfficeId: officeId });
};

const _getOfficeProfileDetails = new CancellableHelper<OfficeProfileDetails>(URL_GET_OFFICE_PROFILE_DETAILS);
export const getOfficeProfileDetails = async (
  officeId: string,
  officeProfileId: number
): Promise<OfficeProfileDetails> => {
  return _getOfficeProfileDetails.get({
    OfficeId: officeId,
    OfficeProfileId: officeProfileId,
  });
};

const _addOfficePofile = new CancellableHelper<null>(URL_POST_ADD_OFFICE_PROFILE);
export const addOfficePofile = async (officeId: string, officeProfile: OfficeProfileDetails): Promise<null> => {
  return _addOfficePofile.post({ OfficeId: officeId }, officeProfile);
};

const _editOfficeProfile = new CancellableHelper<null>(URL_POST_UPDATE_OFFICE_PROFILE);
export const editOfficeProfile = async (
  officeId: string,
  officeProfileId: number,
  officeProfile: OfficeProfileDetails
): Promise<null> => {
  return _editOfficeProfile.post({ OfficeId: officeId, OfficeProfileId: officeProfileId }, officeProfile);
};
//#endregion
